import React, { useEffect, useId, useRef } from 'react'
import ReactDOM from 'react-dom/client'
import mermaid from 'mermaid'
import './style.css'

function LogoMark({ compact = false }) {
  const uid = useId()
  const gradientId = `logoGradient-${uid}`
  const glowId = `logoGlow-${uid}`

  return (
    <svg
      className={`logo-mark${compact ? ' compact' : ''}`}
      viewBox="0 0 760 220"
      role="img"
      aria-label="BRUPPER logo"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#e8eefc" />
          <stop offset="55%" stopColor="#9fb2dd" />
          <stop offset="100%" stopColor="#7c5cff" />
        </linearGradient>
        <filter id={glowId} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="6" result="blur" />
          <feColorMatrix
            in="blur"
            type="matrix"
            values="1 0 0 0 0.1  0 1 0 0 0.1  0 0 1 0 0.2  0 0 0 0.55 0"
            result="glow"
          />
          <feMerge>
            <feMergeNode in="glow" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <rect x="18" y="18" width="724" height="184" rx="92" fill="rgba(255,255,255,0.02)" stroke="rgba(255,255,255,0.08)" />
      <text
        x="58"
        y="157"
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth="10"
        paintOrder="stroke fill"
        filter={`url(#${glowId})`}
        style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 156, fontWeight: 800, letterSpacing: '-0.08em' }}
      >
        brupper
      </text>
      <text
        x="60"
        y="157"
        fill="rgba(232,238,252,0.14)"
        style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 156, fontWeight: 800, letterSpacing: '-0.08em' }}
      >
        brupper
      </text>
    </svg>
  )
}

function Header() {
  return (
    <header>
      <div className="wrap nav">
        <a className="brand" href="#top" aria-label="BRUPPER Gp kezdőlap">
          <LogoMark compact />
          <span>BRUPPER Gp</span>
        </a>
        <nav className="nav-links" aria-label="Fő navigáció">
          <a href="#szolgaltatasok">Szolgáltatások</a>
          <a href="#folyamat">Folyamat</a>
          <a href="#projektek">Projekt</a>
        </nav>
      </div>
    </header>
  )
}

function BrandHero() {
  return (
    <div className="brand-hero card">
      <LogoMark />
      <p>Technológiafüggetlen fejlesztés, tiszta kivitelezés, nextgen kassza szoftver fókusz.</p>
    </div>
  )
}

function Hero() {
  return (
    <section className="hero" id="top">
      <div className="wrap hero-grid">
        <div>
          <span className="eyebrow">Általános megoldások. Tiszta kivitelezés. Gyors szállítás.</span>
          <h1>Minden stacken működő rendszerek, amik tényleg elviszik a hátukon a cégedet.</h1>
          <p className="lead">A BRUPPER Gp egy technológiafüggetlen fejlesztőcég, amely a feladathoz választ stack-et. Fő fókuszunk a nextgen kassza szoftverek fejlesztése: ez a termék már PoC fázisban van, és hamarosan alpha tesztelhető a kasszaszoftver.hu címen.</p>
          <div className="actions">
            <a className="primary" href="#projektek">Nézd meg a projektjeinket</a>
            <a className="secondary" href="#folyamat">Nézd meg a folyamatot</a>
          </div>
        </div>
        <div className="hero-side">
          <BrandHero />
          <aside className="card hero-card" aria-label="Gyors összefoglaló">
            <div className="stats">
              <div className="stat"><strong>Multi-stack</strong><span>Frontendtől backendig, cloudtól integrációig.</span></div>
              <div className="stat"><strong>PoC → Alpha</strong><span>Gyors validálás, fókuszált terméképítés.</span></div>
              <div className="stat"><strong>Nextgen POS</strong><span>Modern kasszarendszerek a valós működésre hangolva.</span></div>
              <div className="stat"><strong>Rugalmas</strong><span>Lehet egyedi fejlesztés, audit vagy teljes termékcsomag.</span></div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  )
}

function Services() {
  return (
    <section className="section" id="szolgaltatasok">
      <div className="wrap">
        <h2>Mit csinálunk</h2>
        <p className="section-lead">Nem „mindenhez értünk” marketingdumával dolgozunk. Inkább úgy: ha kell egy működő rendszer, a BRUPPER Gp leülteti a problémát, felméri a környezetet, és megépíti a legjobb pályát hozzá.</p>
        <div className="grid-3">
          <article className="card feature">
            <span className="tag">Fejlesztés</span>
            <h3>Webes és üzleti rendszerek</h3>
            <p>Kontrollált, skálázható megoldások modern stack-en, a gyors MVP-től a stabil produkcióig.</p>
          </article>
          <article className="card feature">
            <span className="tag">Integráció</span>
            <h3>Rendszerek összekötése</h3>
            <p>Fizetési, számlázási, készletkezelési és egyéb üzleti integrációk tiszta architektúrával.</p>
          </article>
          <article className="card feature">
            <span className="tag">Termék</span>
            <h3>PoC és alpha gyorsítás</h3>
            <p>A validációtól az első használható verzióig rövidítjük az utat, nem nyújtjuk fölöslegesen.</p>
          </article>
        </div>
      </div>
    </section>
  )
}

function ProcessDiagram() {
  const ref = useRef(null)

  useEffect(() => {
    mermaid.initialize({ startOnLoad: false, theme: 'dark', securityLevel: 'loose' })
    mermaid.run({ querySelector: '.mermaid' })
  }, [])

  return (
    <section className="section" id="folyamat">
      <div className="wrap">
        <h2>Fejlesztés és ügyfélkommunikáció</h2>
        <p className="section-lead">A BRUPPER Gp folyamata átlátható: gyors kérdésfeltevés, tiszta scope, ritmusos szállítás, majd visszacsatolás. Így nincs ködösítés, és az ügyfél mindig tudja, hol tartunk.</p>
        <div className="diagram card">
          <div className="mermaid" ref={ref}>
{`flowchart LR
  A[Érdeklődés / megkeresés] --> B[Követelmények tisztázása]
  B --> C[Scope + becslés]
  C --> D[Design / UX irány]
  D --> E[Fejlesztés]
  E --> F[Review + demo]
  F --> G[Finomítás]
  G --> H[Alpha / release]

  subgraph Kommunikáció
    K1[Gyors válaszok]
    K2[Heti státusz]
    K3[Demók és feedback]
    K4[Nyitott döntési pontok]
  end

  B -.-> K1
  C -.-> K2
  F -.-> K3
  G -.-> K4`}
          </div>
        </div>
      </div>
    </section>
  )
}

function Projects() {
  return (
    <section className="section" id="projektek">
      <div className="wrap">
        <h2>Kiemelt projekt</h2>
        <p className="section-lead">A BRUPPER Gp a cég és a márka alapja, erre épül a nextgen kassza szoftveres irány és minden más fejlesztési projekt is.</p>
        <div className="projects">
          <article className="card project">
            <span className="tag">kasszaszoftver.hu</span>
            <h3>Nextgen kassza szoftver</h3>
            <p>PoC állapotban lévő BRUPPER Gp termék, amely hamarosan alpha tesztelhető. Modern, gyors, tiszta felhasználói élményre és üzleti megbízhatóságra épít.</p>
          </article>
          <article className="card project">
            <span className="tag">BRUPPER Gp</span>
            <h3>A cég arca és működési alapja</h3>
            <p>A BRUPPER Gp a cég, a márka és a működés alapja: rugalmas, technológiafüggetlen szemlélet, amire a kasszaszoftveres termékek és más projektek is épülnek.</p>
          </article>
        </div>
      </div>
    </section>
  )
}

function Footer() {
  return (
    <footer className="footer">
      <div className="wrap">
        <p>BRUPPER Gp • Minden stacken, mindenre megoldás.</p>
      </div>
    </footer>
  )
}

function App() {
  return (
    <>
      <Header />
      <main>
        <Hero />
        <Services />
        <ProcessDiagram />
        <Projects />
      </main>
      <Footer />
    </>
  )
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
)
